import { useMemo } from 'react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  APP_SETTINGS_DEFAULTS,
  deliveryConfigOf,
  letterLength,
  letterQuality,
  letterQualityConfigOf,
  type AppSettings,
  type LetterQuality,
  type DeliveryConfig,
  type LetterLengthProgress,
} from '@toiyp/shared';
import { API_ORIGIN } from '../../config';

/**
 * The app's admin-tunable dials (letter minimum, collection rounds, …). Public
 * and the same for everyone; the server applies them regardless, so the UI only
 * mirrors them to show honest counters and hints.
 */
export const appSettingsApi = createApi({
  reducerPath: 'appSettingsApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_ORIGIN}/api`, credentials: 'include' }),
  tagTypes: ['AppSettings'],
  endpoints: (b) => ({
    appSettings: b.query<AppSettings, void>({ query: () => '/settings', providesTags: ['AppSettings'] }),
  }),
});

/** The current settings, or the shipped defaults until (or unless) they load. */
export function useAppSettings(): AppSettings {
  const { data } = appSettingsApi.useAppSettingsQuery(undefined, { pollingInterval: 10 * 60 * 1000 });
  return data ?? APP_SETTINGS_DEFAULTS;
}

export function useDeliveryConfig(): DeliveryConfig {
  const s = useAppSettings();
  return useMemo(() => deliveryConfigOf(s), [s]);
}

/** letterLength against the live minimum. */
export function useLetterLength(body: string): LetterLengthProgress {
  const { minLetterChars } = useAppSettings();
  return useMemo(() => letterLength(body, minLetterChars), [body, minLetterChars]);
}

export interface LetterReadiness extends LetterLengthProgress {
  /** The quality floor's verdict (always ok when the checks are off). */
  quality: LetterQuality;
  /** Long enough and passes the quality floor: what the server will accept. */
  sendable: boolean;
}

/**
 * Length plus the quality floor (`@toiyp/shared` letter-quality), mirroring the
 * server's assertSendable. Quality is only judged once the length is met, so
 * nobody is scolded halfway through their first sentence.
 */
export function useLetterReadiness(body: string): LetterReadiness {
  const settings = useAppSettings();
  return useMemo(() => {
    const length = letterLength(body, settings.minLetterChars);
    const config = letterQualityConfigOf(settings);
    const quality: LetterQuality = length.ready && config ? letterQuality(body, config) : { ok: true };
    return { ...length, quality, sendable: length.ready && quality.ok };
  }, [body, settings]);
}

/** How the post timezone reads to a person: "UK time" for Europe/London. */
export function zoneLabel(timeZone: string): string {
  return timeZone === 'Europe/London' ? 'UK time' : timeZone.replace(/_/g, ' ');
}
